import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { KeyIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import PubkeyForm from './PubkeyForm';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { PubkeyRequest } from '../../../types/models/Pubkey';
import { ApiResponsePubkey } from '../../../types/api/ApiResponsePubkey';

const PubkeyCreate: React.FunctionComponent = () => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const handleSubmit = async (values: PubkeyRequest) => {
    setIsSubmitting(true);
    setValidationErrors({});

    try {
      const response = await apiFetch('/datacenters/pubkeys', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(values),
      });

      const result = (await response.json()) as ApiResponsePubkey;

      if (result.status === 'success' && result.data?.pubkey) {
        addNotification({
          title: 'Public key created',
          srTitle: 'Success alert',
          description: `Public key "${values.label}" has been created successfully.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        navigate(`/datacenter/pubkeys/${result.data.pubkey.id}`);
      } else if (result.status === 'fail') {
        setValidationErrors(result.data as unknown as Record<string, string>);
      } else {
        throw new Error('Failed to create public key');
      }
    } catch (err) {
      addNotification({
        title: 'Error creating public key',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/pubkeys">Public Keys</BreadcrumbItem>
          <BreadcrumbItem isActive>Create</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <KeyIcon />
          </Icon>
          Create public key
        </Content>
        <Content component="p">Add a new SSH public key to your organization</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <PubkeyForm
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitLabel="Create"
          validationErrors={validationErrors}
        />
      </PageSection>
    </>
  );
};

export default PubkeyCreate;
